<template>
  <div class="category-management">
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="650px"
      :before-close="handleClose"
    >
      <div class="category-management-content">
        <!-- 分类列表 -->
        <div class="category-list">
          <div class="section-header">
            <h3>分类列表</h3>
          </div>
          
          <el-tree
            :data="treeData"
            :props="defaultProps"
            node-key="id"
            default-expand-all
            :expand-on-click-node="false"
            highlight-current
            @node-click="handleNodeClick"
          >
            <template #default="{ node, data }">
              <div class="custom-tree-node">
                <span class="category-name">{{ node.label }}</span>
                <span class="operations" v-if="data.parent_id">
                  <el-button 
                    type="primary" 
                    size="small" 
                    link
                    @click.stop="openEditDialog(data)"
                  >
                    编辑
                  </el-button>
                  <el-button 
                    type="danger" 
                    size="small" 
                    link
                    @click.stop="confirmDelete(data)"
                  >
                    删除
                  </el-button>
                </span>
              </div>
            </template>
          </el-tree>
        </div>
        
        <!-- 添加/编辑分类表单 -->
        <div class="category-form">
          <div class="section-header">
            <h3>{{ isEditing ? '编辑分类' : '添加分类' }}</h3>
          </div>
          
          <el-form 
            ref="formRef"
            :model="form"
            :rules="rules"
            label-width="80px"
            size="default"
          >
            <el-form-item label="父分类" prop="parent_id">
              <el-select 
                v-model="form.parent_id" 
                placeholder="选择父分类"
                style="width: 100%"
              >
                <el-option
                  v-for="item in parentCategories"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
            
            <el-form-item label="分类名称" prop="name">
              <el-input v-model="form.name" placeholder="请输入分类名称" />
            </el-form-item>
            
            <el-form-item v-if="!isEditing" label="分类编码" prop="code">
              <el-input v-model="form.code" placeholder="请输入分类编码">
                <template #prepend>
                  <span class="parent-code">{{ parentCode }}</span>
                </template>
              </el-input>
              <div class="form-tip">编码只能包含字母、数字和下划线，创建后不可修改</div>
            </el-form-item>
            
            <el-form-item>
              <el-button 
                type="primary" 
                @click="submitForm" 
                :loading="loading"
              >
                {{ isEditing ? '更新' : '添加' }}
              </el-button>
              <el-button @click="resetForm">重置</el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getCategories, addCategory, updateCategory, deleteCategory } from '../../api/material';

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['update:visible', 'refresh']);

// 对话框可见状态
const dialogVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
});

// 对话框标题
const dialogTitle = '素材分类管理';

// 关闭对话框
const handleClose = () => {
  dialogVisible.value = false;
};

// 加载状态
const loading = ref(false);

// 数据
const treeData = ref([]);
const parentCategories = ref([]);
const selectedNode = ref(null);
const isEditing = ref(false);

// 表单引用
const formRef = ref(null);

// 表单数据
const form = reactive({
  parent_id: '',
  name: '',
  code: '',
  type: ''
});

// 表单验证规则
const rules = {
  parent_id: [
    { required: true, message: '请选择父分类', trigger: 'change' }
  ],
  name: [
    { required: true, message: '请输入分类名称', trigger: 'blur' },
    { min: 2, max: 50, message: '分类名称长度应为2-50个字符', trigger: 'blur' }
  ],
  code: [
    { required: true, message: '请输入分类编码', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9_]+$/, message: '编码只能包含字母、数字和下划线', trigger: 'blur' }
  ]
};

// 计算父分类编码
const parentCode = computed(() => {
  const parent = parentCategories.value.find(item => item.id === form.parent_id);
  return parent ? parent.code + '_' : '';
});

// 表单展示配置
const defaultProps = {
  children: 'children',
  label: 'name'
};

// 监听父分类变化，自动设置类型
watch(() => form.parent_id, (newVal) => {
  const parent = parentCategories.value.find(item => item.id === newVal);
  if (parent) {
    form.type = parent.type;
  }
});

// 加载分类数据
const fetchCategories = async () => {
  try {
    loading.value = true;
    const response = await getCategories();
    treeData.value = response.data.data || [];
    parentCategories.value = treeData.value.map(item => ({
      id: item.id,
      name: item.name,
      code: item.code,
      type: item.type
    }));
    loading.value = false;
  } catch (error) {
    console.error('获取分类失败:', error);
    ElMessage.error('获取分类失败');
    loading.value = false;
  }
};

// 选择节点
const handleNodeClick = (data) => {
  selectedNode.value = data;
};

// 打开编辑对话框
const openEditDialog = (data) => {
  isEditing.value = true;
  form.parent_id = data.parent_id;
  form.name = data.name;
  form.code = data.code;
  form.type = data.type;
  selectedNode.value = data;
};

// 确认删除
const confirmDelete = (data) => {
  ElMessageBox.confirm(
    '确定要删除此分类吗？如果该分类下存在素材，则无法删除。',
    '警告',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(() => {
      handleDelete(data);
    })
    .catch(() => {
      // 取消删除
    });
};

// 处理删除
const handleDelete = async (data) => {
  try {
    loading.value = true;
    await deleteCategory(data.id);
    ElMessage.success('删除分类成功');
    await fetchCategories();
    emit('refresh');
    loading.value = false;
  } catch (error) {
    console.error('删除分类失败:', error);
    if (error.response && error.response.data && error.response.data.message) {
      ElMessage.error(error.response.data.message);
    } else {
      ElMessage.error('删除分类失败');
    }
    loading.value = false;
  }
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        loading.value = true;
        
        if (isEditing.value) {
          // 更新分类
          await updateCategory(selectedNode.value.id, {
            name: form.name
          });
          ElMessage.success('更新分类成功');
        } else {
          // 添加分类
          const formData = {
            name: form.name,
            parent_id: form.parent_id,
            type: form.type,
            code: parentCode.value + form.code
          };
          
          await addCategory(formData);
          ElMessage.success('添加分类成功');
        }
        
        // 重新加载分类并刷新父组件
        await fetchCategories();
        resetForm();
        emit('refresh');
        loading.value = false;
      } catch (error) {
        console.error(isEditing.value ? '更新' : '添加', '分类失败:', error);
        
        if (error.response && error.response.data && error.response.data.message) {
          ElMessage.error(error.response.data.message);
        } else {
          ElMessage.error((isEditing.value ? '更新' : '添加') + '分类失败');
        }
        
        loading.value = false;
      }
    }
  });
};

// 重置表单
const resetForm = () => {
  if (!formRef.value) return;
  
  formRef.value.resetFields();
  isEditing.value = false;
  selectedNode.value = null;
};

// 组件挂载时加载分类
onMounted(() => {
  fetchCategories();
});
</script>

<style scoped>
.category-management-content {
  display: flex;
  gap: 24px;
}

.category-list, .category-form {
  flex: 1;
  min-width: 0;
}

.section-header {
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 8px;
}

.section-header h3 {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.custom-tree-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 8px;
}

.category-name {
  font-size: 14px;
}

.operations {
  display: none;
}

.custom-tree-node:hover .operations {
  display: inline-flex;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.parent-code {
  color: #606266;
  font-size: 13px;
}
</style> 